import 'easymde/dist/easymde.min.css'
import './MarkdownEditor.css'

import type { Optional } from '@festina-life/core'
import { zIndexStyles } from '@festina-life/ui/libs'
import EasyMDE from 'easymde'
import { type CSSProperties, useEffect, useLayoutEffect, useRef } from 'react'

type Props = {
  value: Optional<string>
  onChange?: (nextValue?: Optional<string>) => void
  options?: Optional<EasyMDE.Options>
}

export const MarkdownEditor = ({ value, onChange, options }: Props) => {
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const easyMdeRef = useRef<EasyMDE>(undefined)

  useEffect(() => {
    if (!easyMdeRef.current && textAreaRef.current) {
      easyMdeRef.current = new EasyMDE({
        element: textAreaRef.current,
        sideBySideFullscreen: false,
        unorderedListStyle: '*',
        autofocus: true,
        uploadImage: false,

        ...options,
      })
      easyMdeRef.current.value(value ?? '')

      if (options?.sideBySideFullscreen && easyMdeRef.current) {
        EasyMDE.toggleFullScreen(easyMdeRef.current)
      }

      if (onChange) {
        easyMdeRef.current?.codemirror.on('change', () =>
          onChange(easyMdeRef.current?.value()),
        )
      }
    }
  }, [onChange, options, value])

  useLayoutEffect(
    // useLayoutEffect is used instead of useEffect since it's important to clean up
    // the EasyMDE editor before the textarea is actually unmounted
    () => () => {
      if (easyMdeRef.current) {
        easyMdeRef.current?.cleanup()
        easyMdeRef.current?.toTextArea()
        easyMdeRef.current = undefined
      }
    },
    [],
  )

  return (
    <div
      style={
        {
          // We allow the maxed out zIndex for the EasyMDE toolbar that takes up the whole screen
          '--topBar-zIndex':
            zIndexStyles('yoloDontUse').split('-').at(-1) || '0',
        } as CSSProperties
      }
    >
      <textarea ref={textAreaRef} />
    </div>
  )
}
